import React, { useState, useCallback } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Box, Typography, TextField, Grid } from '@mui/material'
import { useTranslate } from 'ra-core'

// Фиксируем иконки для Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

interface CoordinatePickerProps {
    open: boolean
    onClose: () => void
    onConfirm: (coordinates: string) => void
    initialCoordinates?: string
}

// Компонент для обработки кликов по карте
function MapClickHandler({ onLocationClick }: { onLocationClick: (lat: number, lng: number) => void }) {
    useMapEvents({
        click(e) {
            onLocationClick(e.latlng.lat, e.latlng.lng)
        },
    })
    return null
}

const CoordinatePicker: React.FC<CoordinatePickerProps> = ({
    open,
    onClose,
    onConfirm,
    initialCoordinates = ''
}) => {
    const translate = useTranslate()
    
    // Парсим начальные координаты
    const parseCoordinates = (coordStr: string): [number, number] | null => {
        if (!coordStr) return null
        const parts = coordStr.split(',').map(part => parseFloat(part.trim()))
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            return [parts[0], parts[1]]
        }
        return null
    }

    const initialCoords = parseCoordinates(initialCoordinates)
    const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(
        initialCoords
    )
    
    // Ручной ввод координат
    const [manualLat, setManualLat] = useState(initialCoords ? initialCoords[0].toString() : '')
    const [manualLng, setManualLng] = useState(initialCoords ? initialCoords[1].toString() : '')

    const handleMapClick = useCallback((lat: number, lng: number) => {
        const position: [number, number] = [lat, lng]
        setSelectedPosition(position)
        setManualLat(lat.toFixed(6))
        setManualLng(lng.toFixed(6))
    }, [])

    const handleManualCoordChange = () => {
        const lat = parseFloat(manualLat)
        const lng = parseFloat(manualLng)
        if (!isNaN(lat) && !isNaN(lng)) {
            setSelectedPosition([lat, lng])
        }
    }

    const handleConfirm = () => {
        if (selectedPosition) {
            const coordString = `${selectedPosition[0].toFixed(6)}, ${selectedPosition[1].toFixed(6)}`
            onConfirm(coordString)
        }
        onClose()
    }

    const handleClose = () => {
        // Сбрасываем к исходным значениям при отмене
        const initialCoords = parseCoordinates(initialCoordinates)
        if (initialCoords) {
            setSelectedPosition(initialCoords)
            setManualLat(initialCoords[0].toString())
            setManualLng(initialCoords[1].toString())
        } else {
            setSelectedPosition(null)
            setManualLat('')
            setManualLng('')
        }
        onClose()
    }

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {translate('internal.coordinate_picker.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ height: 400, mb: 2 }}>
                    <MapContainer
                        key={`map-${open}`}
                        center={selectedPosition || [0, 0]}
                        zoom={selectedPosition ? 10 : 2}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {open && <MapClickHandler onLocationClick={handleMapClick} />}
                        {selectedPosition && (
                            <Marker position={selectedPosition} />
                        )}
                    </MapContainer>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {translate('internal.coordinate_picker.hint')}
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={translate('internal.coordinate_picker.latitude')}
                            value={manualLat}
                            onChange={(e) => setManualLat(e.target.value)}
                            onBlur={handleManualCoordChange}
                            type="number"
                            inputProps={{ step: 'any' }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={translate('internal.coordinate_picker.longitude')}
                            value={manualLng}
                            onChange={(e) => setManualLng(e.target.value)}
                            onBlur={handleManualCoordChange}
                            type="number"
                            inputProps={{ step: 'any' }}
                        />
                    </Grid>
                </Grid>
                
                {selectedPosition && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {translate('internal.coordinate_picker.selected')}: {selectedPosition[0].toFixed(6)}, {selectedPosition[1].toFixed(6)}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    disabled={!selectedPosition}
                >
                    {translate('ra.action.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CoordinatePicker
